const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let loadPromise: Promise<void> | null = null;

declare global {
  interface Window {
    __attInitGoogleMaps?: () => void;
    gm_authFailure?: () => void;
  }
}

/** Load Maps API on demand (Places + Geometry). */
export function loadGoogleMaps(): Promise<void> {
  if (typeof window !== "undefined" && window.google?.maps) {
    return Promise.resolve();
  }

  if (loadPromise) return loadPromise;

  if (!MAPS_KEY) {
    return Promise.reject(new Error("Google Maps API key is missing."));
  }

  loadPromise = new Promise((resolve, reject) => {
    const fail = (message: string) => {
      loadPromise = null;
      reject(new Error(message));
    };

    window.gm_authFailure = () => {
      fail(
        "Google Maps API key was rejected. Check billing, enabled APIs, and HTTP referrer restrictions.",
      );
    };

    window.__attInitGoogleMaps = () => {
      if (!window.google?.maps?.places) {
        fail("Google Maps Places library failed to load.");
        return;
      }
      window.dispatchEvent(new CustomEvent("google-maps-loaded"));
      resolve();
    };

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-google-maps-loader="1"]',
    );
    if (existing) {
      if (window.google?.maps) {
        resolve();
        return;
      }
      window.addEventListener("google-maps-loaded", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Google Maps failed to load.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.dataset.googleMapsLoader = "1";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places,geometry&loading=async&callback=__attInitGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error("Google Maps failed to load."));
    document.head.appendChild(script);
  });

  return loadPromise;
}
